import { visualizationActions, visualizationStore } from '../stores/visualization';
// Centralized data service for fetching and processing video data
export class DataService {
    static getInstance() {
        if (!DataService.instance) {
            DataService.instance = new DataService();
        }
        return DataService.instance;
    }
    async loadVideoData() {
        try {
            visualizationActions.setLoading(true);
            // Load the JSON data (keeping existing data source)
            const response = await fetch('./wl_en.json');
            if (!response.ok) {
                throw new Error(`Failed to load data: ${response.statusText}`);
            }
            const rawData = await response.json();
            const videos = this.processVideoData(rawData);
            visualizationActions.setVideos(videos);
            return videos;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            visualizationActions.setError(errorMessage);
            throw error;
        }
    }
    processVideoData(rawData) {
        let extractionOptions = {
            minWordLength: 4,
            maxKeywords: 10,
            customStopWords: []
        };
        const unsubscribe = visualizationStore.subscribe(state => {
            extractionOptions = state.extractionOptions;
        });
        unsubscribe();
        const totalCount = Math.max(1, rawData.length);
        const videos = rawData.map((item, index) => {
            const id = item.vid ?? item.id ?? `video_${index}`;
            const title = item.title_en ?? item.title ?? 'Untitled';
            const description = item.description ?? '';
            const thumbnailUrl = id ? `https://i.ytimg.com/vi/${id}/mqdefault.jpg` : '';
            const keywords = this.extractKeywords(title, description, extractionOptions);
            const power = this.calculatePower(keywords);
            return {
                id,
                title,
                description,
                thumbnailUrl,
                url: id ? `https://www.youtube.com/embed/${id}` : '',
                keywords,
                power,
                position: this.calculatePosition(power, index, totalCount),
                size: this.calculateSize(power)
            };
        });
        // Simple clustering by top keyword label
        const clusterLabelByVideo = new Map();
        videos.forEach(v => {
            const top = v.keywords[0] ?? 'misc';
            clusterLabelByVideo.set(v.id, top);
        });
        videos.forEach(v => {
            v.clusterId = clusterLabelByVideo.get(v.id);
        });
        return videos;
    }
    extractKeywords(title, description, options) {
        // Simple keyword extraction - can be enhanced with the existing keyword extractor
        const text = `${title} ${description}`.toLowerCase();
        const words = text.split(/\s+/)
            .map(w => w.replace(/[^a-z0-9\-\_]/g, ''))
            .filter(word => word.length >= options.minWordLength)
            .filter(word => !this.isStopWord(word, options.customStopWords));
        const limited = words.slice(0, options.maxKeywords);
        return [...new Set(limited)]; // Remove duplicates
    }
    isStopWord(word, customStopWords = []) {
        const base = ['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'who', 'boy', 'did', 'man', 'way', 'she', 'that', 'this', 'with'];
        const set = new Set([...base, ...customStopWords]);
        return set.has(word);
    }
    calculatePower(keywords) {
        // Enhanced power calculation based on keyword relevance
        return Math.min(keywords.length * 0.1 + Math.random() * 0.5, 1.0);
    }
    calculatePosition(power, index, totalCount) {
        // Distribute videos on a sphere using the current list size; guard against NaN with clamping
        const radius = 1300 + (power * 500);
        const denom = Math.max(1, totalCount - 1);
        const t = totalCount <= 1 ? 0.5 : index / denom; // 0..1
        const acosArg = Math.min(1, Math.max(-1, -1 + 2 * t));
        const phi = Math.acos(acosArg);
        const theta = Math.PI * (1 + Math.sqrt(5)) * index; // golden angle
        return {
            x: radius * Math.cos(theta) * Math.sin(phi),
            y: radius * Math.sin(theta) * Math.sin(phi),
            z: radius * Math.cos(phi)
        };
    }
    calculateSize(power) {
        const baseWidth = 240;
        const baseHeight = 160;
        const scale = 0.5 + (power * 1.5);
        return {
            width: baseWidth * scale,
            height: baseHeight * scale
        };
    }
    generateKeywords(videos) {
        const keywordMap = new Map();
        videos.forEach(video => {
            video.keywords.forEach(keyword => {
                if (keywordMap.has(keyword)) {
                    const existing = keywordMap.get(keyword);
                    existing.power += video.power;
                    existing.videos.push(video.id);
                }
                else {
                    keywordMap.set(keyword, {
                        power: video.power,
                        videos: [video.id]
                    });
                }
            });
        });
        const keywords = [];
        let index = 0;
        keywordMap.forEach((data, keyword) => {
            keywords.push({
                keyword,
                power: data.power / data.videos.length,
                videos: data.videos,
                position: this.calculateKeywordPosition(data.power, index++)
            });
        });
        visualizationActions.setKeywords(keywords);
        return keywords;
    }
    // Optional: simple K-means clustering by keyword vector (k fixed)
    assignClustersKMeans(videos, k = 6, iterations = 8) {
        if (videos.length === 0)
            return;
        // Build vocabulary
        const vocab = Array.from(new Set(videos.flatMap(v => v.keywords))).slice(0, 200);
        const vec = (v) => new Float32Array(vocab.map(w => v.keywords.includes(w) ? 1 : 0));
        // Init centroids using first k vectors
        let centroids = videos.slice(0, Math.min(k, videos.length)).map(vec);
        const dot = (a, b) => {
            let s = 0;
            for (let i = 0; i < a.length; i++)
                s += a[i] * b[i];
            return s;
        };
        const norm = (a) => Math.sqrt(dot(a, a)) || 1;
        const cosineSim = (a, b) => dot(a, b) / (norm(a) * norm(b));
        for (let it = 0; it < iterations; it++) {
            // Assign
            const assignments = videos.map(v => {
                const vVec = vec(v);
                let best = 0, bestScore = -Infinity;
                for (let ci = 0; ci < centroids.length; ci++) {
                    const score = cosineSim(vVec, centroids[ci]);
                    if (score > bestScore) {
                        bestScore = score;
                        best = ci;
                    }
                }
                return best;
            });
            // Recompute centroids
            const sums = centroids.map(c => new Float32Array(c.length));
            const counts = centroids.map(() => 0);
            assignments.forEach((ci, i) => {
                const vVec = vec(videos[i]);
                for (let d = 0; d < vVec.length; d++)
                    sums[ci][d] += vVec[d];
                counts[ci]++;
            });
            centroids = centroids.map((_, ci) => {
                const c = sums[ci];
                const count = Math.max(1, counts[ci]);
                for (let d = 0; d < c.length; d++)
                    c[d] = c[d] / count;
                return c;
            });
            // Early stop if any
            if (counts.every(c => c > 0)) { /* keep going for fixed iterations */ }
        }
        // Final assignment
        videos.forEach(v => {
            const vVec = vec(v);
            let best = 0, bestScore = -Infinity;
            for (let ci = 0; ci < centroids.length; ci++) {
                const score = cosineSim(vVec, centroids[ci]);
                if (score > bestScore) {
                    bestScore = score;
                    best = ci;
                }
            }
            v.clusterId = `cluster_${best}`;
        });
    }
    calculateKeywordPosition(power, index) {
        // Position keywords on a sphere surface
        const radius = 800;
        const phi = Math.acos(-1 + (2 * index) / 50);
        const theta = Math.sqrt(50 * Math.PI) * phi;
        return {
            x: radius * Math.cos(theta) * Math.sin(phi),
            y: radius * Math.sin(theta) * Math.sin(phi),
            z: radius * Math.cos(phi)
        };
    }
}
